// Copyright (c) 2026 PGNS LLC

import { useCallback, useEffect, useRef, useState } from 'react';
import type { History, IncidentList, Summary } from '@/lib/api';
import { fetchHistory, fetchIncidents, fetchSummary } from '@/lib/api';

/** `/v1/summary` is edge-cached for 20s; polling faster only burns battery. */
const POLL_INTERVAL_MS = 30_000;

export interface StatusData {
  summary: Summary | null;
  history: History | null;
  incidents: IncidentList | null;
  /** True until the first load settles, either way. */
  loading: boolean;
  /** Set when the last attempt failed. Previously-loaded data is kept. */
  error: Error | null;
  lastLoadedAt: number | null;
  refresh: () => void;
}

/**
 * Load the status payloads and re-poll on an interval.
 *
 * A failed poll never clears what is already on screen: a stale strip plus an
 * explicit "status data unavailable" banner tells a reader more than a page
 * that empties itself the moment the API blinks. `loading` only gates the very
 * first render, so a background refresh never flashes a skeleton.
 */
export function useStatusData(): StatusData {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [incidents, setIncidents] = useState<IncidentList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Effect cleanup aborts in-flight requests, and an aborted fetch must not be
  // reported as an outage — the page is unmounting, not the platform.
  const abortedRef = useRef(false);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    abortedRef.current = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function load() {
      try {
        const [nextSummary, nextHistory, nextIncidents] = await Promise.all([
          fetchSummary(controller.signal),
          fetchHistory(undefined, controller.signal),
          fetchIncidents(undefined, controller.signal),
        ]);
        if (abortedRef.current) return;
        setSummary(nextSummary);
        setHistory(nextHistory);
        setIncidents(nextIncidents);
        setError(null);
        setLastLoadedAt(Date.now());
      } catch (cause) {
        if (abortedRef.current) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      } finally {
        if (!abortedRef.current) {
          setLoading(false);
          timer = setTimeout(() => void load(), POLL_INTERVAL_MS);
        }
      }
    }

    void load();

    return () => {
      abortedRef.current = true;
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [reloadToken]);

  return { summary, history, incidents, loading, error, lastLoadedAt, refresh };
}
